import { createRxDatabase, addRxPlugin } from "rxdb";
import { getRxStorageIndexedDB } from "rxdb-premium/plugins/storage-indexeddb";
import { RxDBQueryBuilderPlugin } from "rxdb/plugins/query-builder";
import { RxDBUpdatePlugin } from "rxdb/plugins/update";
import { RxDBMigrationSchemaPlugin } from "rxdb/plugins/migration-schema";
import { RxDBLeaderElectionPlugin } from "rxdb/plugins/leader-election";

import { SupabaseReplication } from "pkg-rxdb-supabase";
import { supabasePlugin } from "@/plugins/supabase.ts";
import Constants from "@/utils/constants";
import Helper from "@/utils/helper";
import { setPremiumFlag } from "rxdb-premium/plugins/shared";

// hard coded schema for rxdb_schemas table
import rxdbSchemasConfig from "./config/rxdb_schemas_config.json";
import { useRxDBStore } from "@/stores/rxDBStore";
import { useProfileStore } from "@/stores/profileStore";
import { useUserStore } from "@/stores/userStore";
import dbOperationsRxDB from "@/utils/dbOperationsRxDB";
import Exception from "@/utils/exception";
// Add plugins
addRxPlugin(RxDBQueryBuilderPlugin);
addRxPlugin(RxDBUpdatePlugin);
addRxPlugin(RxDBMigrationSchemaPlugin);
addRxPlugin(RxDBLeaderElectionPlugin);

// Replication configuration
let REPLICATION_CONFIG = {
  [Constants.enums.RXDB_COLLECTIONS_ENUM.RXDB_SCHEMAS]: {
    table: Constants.enums.TABLES_NAME_ENUM.RXDB_SCHEMAS_TABLE,
    replicationIdentifier: `${import.meta.env.VITE_SUPABASE_URL}-${Constants.enums.TABLES_NAME_ENUM.RXDB_SCHEMAS_TABLE}`,
    batchSize: 1000,
    fetchOptions: {}
  }
};

const getForms = async (collectionName, queryParams = {}) => {
  try {
    const collection = await getCollection(collectionName);

    const forms = await collection
      .find({
        selector: queryParams.selector || {},
        sort: [{ remote_updated_at: "desc" }]
      })
      .exec();

    return forms;
  } catch (error) {
    console.error("Error fetching forms:", error);
    throw error;
  }
};

// Database configuration
const dbConfig = {
  name: `PHOENIX_RXDB_${Helper.getCurrentEnvironment()}`, // database name in the browser
  storage: getRxStorageIndexedDB(),
  multiInstance: true,
  closeDuplicates: true
};

// Singleton instance
let dbInstance = null;
const replicationInstances = new Map();

/**
 * Set up replication for a collection
 */
const setupReplication = async (collection, collectionName) => {
  const config = REPLICATION_CONFIG[collectionName];
  if (!config) {
    throw new Error(
      `No replication configuration found for collection: ${collectionName}`
    );
  }

  const replicationInstance = new SupabaseReplication({
    supabaseClient: supabasePlugin.client,
    collection: collection,
    listen: true,
    table: config.table,
    retryTime: Helper.isWifiOnlyMode()
      ? 600e3
      : Helper.isCellularOnlyMode()
        ? 60e3
        : 30e3, // 10 mins for wifi-only, 60 secs for cellular-only, 30 secs for wifi+cellular
    deletedField: "is_deleted",
    waitForLeadership: false,
    replicationIdentifier:
      config.replicationIdentifier + import.meta.env.VITE_SUPABASE_URL,
    pull: {
      lastModifiedField: "remote_updated_at",
      batchSize: config.batchSize || 1000,
      fetchOptions: config.fetchOptions || {}
    },
    push: {}
  });

  replicationInstance.error$.subscribe((error) => {
    console.log("RX SYNC ERROR :", error.name);
    Exception.catchException("RX SYNC ERROR :", error.name);
  });

  replicationInstance.active$.subscribe((isActive) => {
    useRxDBStore().setCollectionSyncStatus(collectionName, isActive);
  });

  replicationInstance.received$.subscribe((data) => {
    // console.log("SYNC RECEIVED :", collectionName, data.id, data);
  });
  replicationInstance.sent$.subscribe(async (data) => {
    // console.log("SYNC SENT :", collectionName, data.id, data);
    useRxDBStore().updateLocalSyncQueue(collectionName, data.id, true);
  });
  replicationInstance.canceled$.subscribe((data) => {});

  return replicationInstance;
};

const fetchUserProfile = async (accessGroups = []) => {
  if (Helper.getCurrentUserId()) {
    try {
      // if id exists then fetch the profile from the database
      const _res = await useProfileStore().fetchProfile(
        Helper.getCurrentUserId()
      );
      if (_res?.data) {
        if (_res.data.access_groups) {
          if (_res.data.access_groups.length > 0) {
            return _res.data.access_groups;
          } else {
            return [...accessGroups, ..._res.data.access_groups];
          }
        }
      }
    } catch (error) {
      return accessGroups;
    }
  }
};

const prepareQuery = (accessGroups) => {
  return accessGroups
    ? {
        selector: {
          $or: accessGroups.map((group) => ({
            category: {
              $elemMatch: {
                $eq: group
              }
            }
          }))
        }
      }
    : {};
};

const initiateReplication = async (
  collectionName,
  waitForInitialSync = false
) => {
  const collection = await getCollection(collectionName);
  startCollectionObservable(collection);
  const replicationInstance = await setupReplication(
    collection,
    collectionName,
    waitForInitialSync
  );
  replicationInstances.set(collectionName, replicationInstance);
};

const setReplicationConfig = (formCopy) => {
  // Set up replication config for this collection
  REPLICATION_CONFIG[formCopy.table_name] = {
    table: formCopy.table_name,
    replicationIdentifier: `${import.meta.env.VITE_SUPABASE_URL}-${formCopy.table_name}`,
    batchSize: formCopy.batch_size || 1000,
    fetchOptions: formCopy.fetch_options || {}
  };
};

/**
 * Initialize the RXDB database
 */
export const initDatabase = async (from) => {
  console.log(
    "INIT DATABASE called ----------------",
    from,
    useUserStore().isOnline,
    useUserStore().isUserSignedIn()
  );
  // if initProcessStarted is true then return the dbInstance
  if (dbInstance) {
    console.log("dbInstance already exists ----------------");
    return dbInstance;
  }

  // set flags
  setPremiumFlag();
  useRxDBStore().setAllCollectionsAdded(false);
  try {
    // create rxdb instance
    dbInstance = await createRxDatabase(dbConfig);
    // start migration observable
    startMigrationObservable();
    // get migration strategies for rxdb collection
    const _rxDbMigrationStrategies =
      Helper.getMigrationStrategies(rxdbSchemasConfig);
    // add initial collections
    let collections = {
      [Constants.enums.RXDB_COLLECTIONS_ENUM.RXDB_SCHEMAS]:
        Helper.getCollectionConfig({
          ...rxdbSchemasConfig,
          migrationStrategies: _rxDbMigrationStrategies
        })
    };
    // need to add collections here so that we can pull configured collections from the database
    await dbInstance.addCollections(collections);
    // after adding collection start replication for this
    for (const [collectionName] of Object.entries(REPLICATION_CONFIG)) {
      await initiateReplication(collectionName, true);
    }
    // mow no more need of the initial rxDB collection to replication
    delete REPLICATION_CONFIG[
      Constants.enums.RXDB_COLLECTIONS_ENUM.RXDB_SCHEMAS
    ];
    // get all forms from the schemas collection
    let forms = [];
    // Only proceed if database instance exists
    if (dbInstance) {
      // default access groups
      let _accessGroups = ["GENERAL"];
      // fetch access groups from the user's network profile
      _accessGroups = await fetchUserProfile(_accessGroups);
      console.log("access groups :", _accessGroups);
      // prepare query to pull only the forms that are accessible to the user
      const _query = prepareQuery(_accessGroups);
      console.log("query :", _query);
      try {
        let attempts = 1;
        const maxAttempts = 30; // Try for 30 seconds max

        while (forms.length === 0 && attempts <= maxAttempts) {
          forms = await getForms(
            Constants.enums.RXDB_COLLECTIONS_ENUM.RXDB_SCHEMAS,
            { selector: _query || {} }
          );

          if (forms.length === 0) {
            await new Promise((resolve) => setTimeout(resolve, 1000)); // Wait 1 second
            attempts++;
          }
        }
        Exception.catchException(
          `RXDB forms fetched on attempt (${attempts}) and found total (${forms.length}) : ${forms.map((form) => form.table_name).join(", ")}`
        );
        console.log(
          `RXDB forms fetched on attempt (${attempts}) and found total (${forms.length})`
        );
      } catch (e) {
        console.error("Error fetching forms:", e);
      }
      // Process each form and create collections
      for (const form of forms) {
        // we will add all the list of collection that we need to wait before moving forward
        if (form.wait_until_finish)
          useRxDBStore().setInitialCollection(form.table_name);
        // Create deep copy to avoid mutations
        const formCopy = Helper.deepCopy(form);
        // test case to check if it's already exists in the collections
        try {
          await getCollection(formCopy.table_name);
          continue;
        } catch (error) {}
        const configObj = formCopy.config_obj;

        // Parse JSON and convert string functions back to actual functions
        const migrationStrategies = Helper.getMigrationStrategies(formCopy);

        // Create collection config for this form
        const newCollections = {
          [formCopy.table_name]: Helper.getCollectionConfig(
            migrationStrategies
              ? {
                  ...configObj,
                  migrationStrategies
                }
              : configObj
          )
        };

        // Add to master collections object
        collections = { ...collections, ...newCollections };

        if (formCopy.fetch_options) {
          formCopy.fetch_options.queryParams &&
            formCopy.fetch_options.queryParams.forEach((option) => {
              if (
                option.value ===
                Constants.enums.FIXED_FETCH_OPTIONS_KEYS.USER_ID
              ) {
                if (Helper.getCurrentUserId()) {
                  option.value = Helper.getCurrentUserId();
                } else {
                  throw new Error(
                    "RXDB: User ID not found so terminating the process."
                  );
                }
              }
            });
        }
        // Set up replication config for this collection
        setReplicationConfig(formCopy);
        // Adding the collections pulled from db to the rxDB instance
        await dbInstance.addCollections(newCollections);
        // start replication for this collection
      }
      // console success message for the number of collections added
      Helper.consoleSuccess(
        `RXDB Database initialized successfully, (${Object.keys(dbInstance.collections).length}) collections: \n ${Object.keys(dbInstance.collections)}`
      );
    }
    
    dbInstance.waitForLeadership().then(async () => {
      console.log("Long lives the king! RXDB"); // <- runs when db becomes leader
      for (const form of forms) {
        await initiateReplication(form.table_name, form.wait_until_finish);
      }
    });

    // mark first sync completed
    useRxDBStore().markFirstSyncCompleted();
    console.log("dbInstance init process done ----------------");
    return dbInstance;
  } catch (error) {
    Exception.catchException(error);
    console.error("Failed to initialize RXDB:", error);
    throw error;
  } finally {
  }
};

// Function to start observables for collection events
const startCollectionObservable = (collection) => {
  // Subscribe to all changes
  collection.$.subscribe((changeEvent) => {
    // console.log(
    //   `---------------- RXDB COLLECTION CHANGE ----------------\n\nCollection: ${collection.name}\n\nChange Event: ${changeEvent.operation}\n\nDocument ID: ${changeEvent.documentId}`
    // );
  });
};

// Function to listen to all migration states
const startMigrationObservable = () => {
  const allStatesObservable = dbInstance.migrationStates();
  allStatesObservable.subscribe((allStates) => {
    allStates.forEach((migrationState) => {
      // console.warn(
      //   `---------------- RXDB MIGRATION STATE ----------------\nMigration state for collection: ${migrationState.collection.name}\n----------------------------------------------------`
      // );
    });
  });
};

/**
 * Get the database instance
 */
export const getDatabase = async () => {
  try {
    if (!dbInstance) {
      return await initDatabase("getDatabase");
    }
    return dbInstance;
  } catch (error) {
    console.error("Failed to get RXDB database:", error);
  }
};

// does database exist
export const doesDatabaseExist = async () => {
  return dbInstance ? true : false;
};

/**
 * Get a collection from the database
 */
export const getCollection = async (collectionName) => {
  const db = await getDatabase();
  if (!db[collectionName]) {
    throw new Error(`Collection ${collectionName} not found`);
  }

  return db[collectionName];
};

/**
 * Destroy the database instance
 */
export const destroyDatabase = async () => {
  if (dbInstance) {
    await dbInstance.destroy();
    dbInstance = null;
  }
};

/**
 * Get the replication instance for a specific collection
 */
export const getReplication = (collectionName) => {
  return replicationInstances.get(collectionName);
};

/**
 * Get all replication instances
 */
export const getAllReplications = () => {
  return replicationInstances;
};
